import { dcos, dsin, PI } from '../sim/dmath';
import type { Rng } from './prng';
import type { Vec2 } from './types';

/**
 * Circuits built the way real ones are drawn: straights joined by corners of
 * a known kind (hairpins, 90s, fast esses, chicanes, double apexes, sweepers,
 * carousels), rather than a smooth loop through a handful of points.
 *
 * A layout is a chain of pieces. Each piece is a straight or a constant-radius
 * arc. The chain closes exactly: one "closing" arc takes whatever turn is left,
 * and the straights stretch or shrink (least change, weighted) until the end
 * meets the start. The curvature is then eased (so every corner gets a
 * transition, like a clothoid) and integrated back into points.
 *
 * Two families:
 * - "loop": a simple circuit turning once around, with a random sequence of
 *   corner modules, sometimes a right-hander that dents it (an L or a boot).
 * - "figure8": two lobes turning opposite ways, joined by two straights that
 *   cross in an X, Suzuka-style. One of them dips under the other.
 *
 * Deterministic: dmath trig, plain arithmetic, the day's Rng.
 */
export type LayoutFamily = 'loop' | 'figure8';

/**
 * Each day's showpiece, after a famous corner or stretch (never a copy): one a
 * day, rotating so none comes back for twelve days.
 */
export const SIGNATURES = [
  'esses', // Silverstone's Maggotts-Becketts, Suzuka's S curves
  'corkscrew', // Laguna Seca: over a blind crest into a plunging left-right
  'banking', // Monza's Sopraelevata: a steep banked curve with a wall on top
  'karussell', // the Nordschleife's banked concrete carousel
  'crossover', // Suzuka: a figure of eight, one road under the other
  'eauRouge', // Spa: down into the valley, flick left-right-left, up the hill
  'climb', // COTA turn 1: a steep climb to a hairpin at the top
  'streets', // Monaco: the tightest hairpin and a double chicane between walls
  'tripleApex', // Istanbul turn 8: a long left with three apexes
  'stadium', // Hockenheim's Motodrom: a run of 90s before the grandstands
  'jumps', // the Nordschleife's Flugplatz and Pflanzgarten: fast crests that throw the car light
  'mountain', // Bathurst: a long climb, then plunging esses down the hill
] as const;
export type Signature = (typeof SIGNATURES)[number];

interface Straight {
  kind: 's';
  len: number;
  /** Nominal range, for drawing it again when the chain won't close. */
  lo: number;
  hi: number;
  min: number;
  max: number;
  /** How readily it stretches to close the loop (0 = fixed). */
  w: number;
  /** The main straight (the start/finish line goes on it). */
  main?: boolean;
}
interface Arc {
  kind: 'a';
  r: number;
  /** Signed turn, radians: + left, - right. */
  ang: number;
  /** Takes whatever turn is left over. */
  closing?: boolean;
  /** Banking, as the slope across the road (tan of the angle), raised on the outside. */
  bank?: number;
}
type Piece = Straight | Arc;

interface Pose {
  x: number;
  y: number;
  h: number;
}

const DEG = PI / 180;

/** A named stretch of the lap, as fractions of it from the start of the main straight (may wrap). */
export interface Section {
  name: string;
  from: number;
  to: number;
}

export interface Layout {
  family: LayoutFamily;
  signature: Signature;
  /** Evenly spaced (about 1 m) closed polyline, main straight first. */
  pts: Vec2[];
  /** Banking per point (slope across the road, left side higher positive), or null if none. */
  bank: number[] | null;
  /** The signature's stretch (and its parts), for hills, walls and scenery. */
  sections: Section[];
  /** Module names in order, for the dev panel. */
  modules: string[];
}

// ---------------------------------------------------------------------------
// Pieces and modules
// ---------------------------------------------------------------------------

/** A straight of random nominal length in [lo, hi]. */
const S = (rng: Rng, lo: number, hi: number, w = 1, min = 12, max = 1e9): Straight => ({ kind: 's', len: rng.range(lo, hi), lo, hi, min, max, w });
const A = (r: number, deg: number): Arc => ({ kind: 'a', r, ang: deg * DEG });

interface Module {
  name: string;
  pieces: Piece[];
  /** Named stretches inside the module: [section name, first piece, last piece]. */
  parts?: [string, number, number][];
}

/** Every corner module, turning towards `dir` (+1 left, -1 right). */
function moduleOf(rng: Rng, name: string, dir: number): Module {
  const m = (pieces: Piece[], parts?: [string, number, number][]): Module => ({ name, pieces, parts: parts ?? [[name, 0, pieces.length - 1]] });
  const gap = (lo: number, hi: number): Straight => S(rng, lo, hi, 0, 0);
  switch (name) {
    // --- everyday corners
    case 'hairpin':
      return m([A(rng.range(17, 24), dir * rng.range(165, 188))]);
    case 'tightHairpin':
      return m([A(rng.range(14.5, 16.5), dir * rng.range(172, 196))]);
    case 'ninety':
      return m([A(rng.range(24, 36), dir * rng.range(80, 100))]);
    case 'tight':
      return m([A(rng.range(20, 28), dir * rng.range(105, 140))]);
    case 'medium':
      return m([A(rng.range(36, 55), dir * rng.range(55, 95))]);
    case 'sweeper':
      return m([A(rng.range(65, 100), dir * rng.range(50, 85))]);
    case 'bigSweeper':
      // Suzuka's 130R, Copse: long, fast, committed.
      return m([A(rng.range(95, 130), dir * rng.range(55, 80))]);
    case 'kink':
      return m([A(rng.range(120, 190), dir * rng.range(16, 28))]);
    case 'double90': {
      // Monza's Lesmos, Suzuka's Degners: two quick 90s the same way.
      const r = rng.range(28, 40);
      return m([A(r, dir * rng.range(70, 90)), S(rng, 22, 45, 0.3, 12), A(r * rng.range(0.85, 1.1), dir * rng.range(70, 90))]);
    }
    case 'doubleApex': {
      // Spa's Pouhon, Suzuka's Spoon.
      const r = rng.range(28, 40);
      return m([A(r, dir * rng.range(55, 75)), S(rng, 14, 30, 0.3, 8), A(r * rng.range(0.9, 1.15), dir * rng.range(55, 75))]);
    }
    case 'parabolica': {
      // Tightening in, opening out onto the straight.
      const r = rng.range(24, 30);
      return m([A(r, dir * rng.range(55, 70)), A(r * 1.6, dir * rng.range(50, 65)), A(r * 2.8, dir * rng.range(45, 60))]);
    }
    case 'chicane': {
      // Monza's Rettifilo: a quick left-right (or right-left) that barely changes the heading.
      const r = rng.range(18, 24);
      const a = rng.range(42, 58);
      return m([A(r, dir * a), gap(3, 10), A(r, -dir * a * rng.range(0.9, 1.1))]);
    }
    case 'busStop': {
      // Spa's Bus Stop, Suzuka's Casio: in, across and back out.
      const r = rng.range(18, 24);
      const a = rng.range(38, 50);
      return m([A(r, dir * a), gap(2, 8), A(r, -dir * a * 2), gap(2, 8), A(r, dir * a)]);
    }
    // --- the signatures
    case 'esses': {
      // Big, quick direction changes flicking left, right, left, right: no straight line through.
      const pieces: Piece[] = [];
      const count = rng.int(3, 5);
      let d = dir;
      for (let k = 0; k < count; k++) {
        const edge = k === 0 || k === count - 1;
        pieces.push(A(rng.range(32, 44), d * (edge ? rng.range(58, 78) : rng.range(100, 125))));
        if (k < count - 1) pieces.push(gap(0, 4));
        d = -d;
      }
      return m(pieces);
    }
    case 'corkscrew':
      // A blind crest into a tight left-right that drops away underneath (the hills do the dropping).
      return m([S(rng, 50, 80, 0.4, 35), A(rng.range(22, 28), dir * rng.range(60, 75)), gap(0, 3), A(rng.range(24, 30), -dir * rng.range(70, 90)), S(rng, 30, 50, 0.3, 20)]);
    case 'banking': {
      // A steep banked curve, 30-odd degrees, a wall along its top.
      const a: Arc = A(rng.range(52, 66), dir * rng.range(150, 180));
      a.bank = rng.range(0.55, 0.66);
      return m([a]);
    }
    case 'karussell': {
      // A tight carousel on a concrete bank: hook in and hang on.
      const a: Arc = A(rng.range(28, 34), dir * rng.range(205, 235));
      a.bank = rng.range(0.34, 0.42);
      return m([a]);
    }
    case 'eauRouge':
      // Fast left-right-left through the bottom of a valley and up the other side.
      return m([A(rng.range(60, 80), dir * rng.range(28, 38)), gap(0, 3), A(rng.range(42, 55), -dir * rng.range(55, 70)), gap(0, 3), A(rng.range(55, 75), dir * rng.range(32, 45)), S(rng, 50, 80, 0.4, 35)]);
    case 'climb':
      // A long drag uphill, then a hairpin at the top.
      return m([S(rng, 110, 150, 0.5, 90), A(rng.range(16, 21), dir * rng.range(170, 190))]);
    case 'streets': {
      // The tightest hairpin, a short blast, then a double chicane: all of it between walls.
      const r = rng.range(19, 23);
      const a = rng.range(45, 60);
      return m([
        A(rng.range(14.5, 16), dir * rng.range(175, 195)),
        S(rng, 30, 50, 0.3, 20),
        A(r, -dir * a),
        gap(2, 6),
        A(r, dir * a),
        S(rng, 12, 22, 0.2, 8),
        A(r, dir * a * 0.9),
        gap(2, 6),
        A(r, -dir * a * 0.9),
        S(rng, 15, 30, 0.2, 10),
      ]);
    }
    case 'tripleApex':
      return m([A(rng.range(42, 54), dir * rng.range(62, 76)), gap(6, 14), A(rng.range(44, 56), dir * rng.range(62, 76)), gap(6, 14), A(rng.range(46, 58), dir * rng.range(62, 76))]);
    case 'stadium': {
      // A run of 90s close together: in, out, round and back.
      const r = (): number => rng.range(20, 27);
      const q = (): number => rng.range(84, 96);
      return m([A(r(), dir * q()), S(rng, 24, 40, 0.3, 16), A(r(), -dir * q()), S(rng, 24, 40, 0.3, 16), A(r(), -dir * q()), S(rng, 24, 40, 0.3, 16), A(r(), dir * q())]);
    }
    case 'jumps':
      // A long, fast, barely-bending stretch over a run of crests.
      return m([A(rng.range(260, 330), dir * rng.range(22, 32))]);
    case 'mountain': {
      // Up: a climbing sweeper and a kink. Down: plunging esses.
      const up: Piece[] = [A(rng.range(80, 110), dir * rng.range(30, 45)), S(rng, 40, 70, 0.4, 25), A(rng.range(60, 85), -dir * rng.range(30, 45)), S(rng, 40, 60, 0.3, 25)];
      const down: Piece[] = [A(rng.range(34, 42), dir * rng.range(65, 85)), gap(0, 3), A(rng.range(34, 42), -dir * rng.range(90, 110)), gap(0, 3), A(rng.range(38, 46), dir * rng.range(60, 80))];
      return m([...up, ...down], [
        ['mountainUp', 0, up.length - 1],
        ['mountainDown', up.length, up.length + down.length - 1],
      ]);
    }
    default:
      throw new Error('unknown module ' + name);
  }
}

function turnOf(pieces: Piece[]): number {
  let t = 0;
  for (const p of pieces) if (p.kind === 'a') t += p.ang;
  return t;
}

function pick<T>(rng: Rng, items: [T, number][]): T {
  let total = 0;
  for (const [, w] of items) total += w;
  let r = rng.next() * total;
  for (const [v, w] of items) {
    r -= w;
    if (r < 0) return v;
  }
  return items[items.length - 1][0];
}

// ---------------------------------------------------------------------------
// Closing a chain
// ---------------------------------------------------------------------------

function advance(p: Pose, piece: Piece): Pose {
  if (piece.kind === 's') return { x: p.x + piece.len * dcos(p.h), y: p.y + piece.len * dsin(p.h), h: p.h };
  const sign = piece.ang > 0 ? 1 : -1;
  const cx = p.x - sign * piece.r * dsin(p.h);
  const cy = p.y + sign * piece.r * dcos(p.h);
  const h = p.h + piece.ang;
  return { x: cx + sign * piece.r * dsin(h), y: cy - sign * piece.r * dcos(h), h };
}

/**
 * Make the chain end exactly at `end` with the total turn `turn`: the closing
 * arc takes the leftover turn, then the straights change length by the least
 * weighted amount (a 2x2 solve; any straight pushed past its limits is pinned
 * there and the rest solve again). Returns false if it can't be done sensibly.
 */
function closeChain(rng: Rng, pieces: Piece[], start: Pose, end: Vec2, turn: number): boolean {
  const closing = pieces.find((p): p is Arc => p.kind === 'a' && !!p.closing);
  if (!closing) return false;
  const r0 = closing.r;
  for (let attempt = 0; attempt < 5; attempt++) {
    if (attempt > 0) {
      // Draw the stretchable straights again and retry.
      for (const p of pieces) if (p.kind === 's' && p.w > 0) p.len = rng.range(p.lo, p.hi);
      closing.r = r0;
    }
    if (closeOnce(pieces, closing, start, end, turn)) return true;
  }
  return false;
}

function closeOnce(pieces: Piece[], closing: Arc, start: Pose, end: Vec2, turn: number): boolean {
  const sign = closing.ang > 0 ? 1 : -1;
  const want = turn - (turnOf(pieces) - closing.ang);
  // The closing corner must still turn its own way, and not absurdly far.
  if (want * sign < 25 * DEG || want * sign > 200 * DEG) return fail('closing-turn');
  closing.ang = want;
  // Heavier turns get a tighter radius, so they stay corners rather than loops.
  const deg = Math.abs(want) / DEG;
  closing.r = deg > 150 ? Math.min(closing.r, 34) : deg > 100 ? Math.min(closing.r, 50) : closing.r;

  const free = new Set<Straight>();
  for (const p of pieces) if (p.kind === 's' && p.w > 0) free.add(p);
  for (let pass = 0; pass < 6; pass++) {
    let pose = start;
    const dirs = new Map<Straight, [number, number]>();
    for (const p of pieces) {
      if (p.kind === 's') dirs.set(p, [dcos(pose.h), dsin(pose.h)]);
      pose = advance(pose, p);
    }
    const gx = end.x - pose.x;
    const gy = end.y - pose.y;
    if (gx * gx + gy * gy < 1e-6) return true;
    // Least weighted change: dL_j = w_j^2 u_j . lambda, with M lambda = gap.
    let m00 = 0;
    let m01 = 0;
    let m11 = 0;
    for (const s of free) {
      const [ux, uy] = dirs.get(s)!;
      const w2 = s.w * s.w;
      m00 += w2 * ux * ux;
      m01 += w2 * ux * uy;
      m11 += w2 * uy * uy;
    }
    const det = m00 * m11 - m01 * m01;
    if (Math.abs(det) < 1e-9) return fail('singular');
    const lx = (m11 * gx - m01 * gy) / det;
    const ly = (m00 * gy - m01 * gx) / det;
    let pinned = false;
    for (const s of free) {
      const [ux, uy] = dirs.get(s)!;
      const len = s.len + s.w * s.w * (ux * lx + uy * ly);
      if (len < s.min) {
        s.len = s.min;
        free.delete(s);
        pinned = true;
      } else if (len > s.max) {
        s.len = s.max;
        free.delete(s);
        pinned = true;
      } else s.len = len;
    }
    if (!pinned) return true;
    if (free.size < 2) return fail('pinned');
  }
  return fail('passes');
}

/** Why the last chain didn't work out (diagnostics only). */
export let lastFail = '';
function fail(why: string): false {
  lastFail = why;
  return false;
}

// ---------------------------------------------------------------------------
// Families
// ---------------------------------------------------------------------------

interface Plan {
  pieces: Piece[];
  modules: string[];
  /** Named stretches: [name, first piece, last piece] (indices into pieces). */
  parts: [string, number, number][];
  /** Pose the chain starts from. */
  start: Pose;
}

/** Filler corners that keep turning the loop's way, with how often they come up. */
const TURNS: [string, number][] = [
  ['ninety', 3],
  ['medium', 3],
  ['sweeper', 2],
  ['bigSweeper', 1.2],
  ['tight', 1.5],
  ['double90', 1.4],
  ['doubleApex', 1.4],
  ['hairpin', 1],
  ['tightHairpin', 0.4],
  ['parabolica', 0.8],
];
/** Fillers that barely change the heading. */
const FLICKS: [string, number][] = [
  ['chicane', 1.6],
  ['busStop', 1],
  ['kink', 1.2],
];
/** Corners that turn against the loop, denting it: an L, a boot, a notch. */
const DENTS: [string, number][] = [
  ['medium', 2],
  ['ninety', 1.5],
  ['sweeper', 1.2],
  ['double90', 0.8],
  ['tight', 0.6],
];
const HEAVY_BRAKING: [string, number][] = [
  ['hairpin', 3],
  ['ninety', 2.5],
  ['tight', 2],
  ['tightHairpin', 0.6],
];
/** How often a filler may appear in one circuit. */
const MAX_USES: Record<string, number> = { ninety: 2, medium: 2 };

/**
 * Corner modules one after another, short straights between, keeping the turn
 * so far inside a budget so the closing corner is left a sensible share (35 to
 * 150 degrees). `dir` is the way the whole chain turns. The day's signature
 * goes in at `sigSlot`; fillers never repeat (bar a second 90 or medium).
 */
function planModules(
  rng: Rng,
  plan: Plan,
  dir: number,
  target: number,
  slots: number,
  opts: { signature: Module | null; sigSlot: number; rights: number; straight: [number, number] },
): boolean {
  const { pieces, modules, parts } = plan;
  const [sLo, sHi] = opts.straight;
  const used = new Map<string, number>();
  for (const name of modules) used.set(name, (used.get(name) ?? 0) + 1);
  const add = (mod: Module): void => {
    const base = pieces.length;
    pieces.push(...mod.pieces);
    modules.push(mod.name);
    used.set(mod.name, (used.get(mod.name) ?? 0) + 1);
    for (const [name, a, b] of mod.parts ?? []) parts.push([name, base + a, base + b]);
  };
  const free = (items: [string, number][]): [string, number][] => items.filter(([n]) => (used.get(n) ?? 0) < (MAX_USES[n] ?? 1));
  const lo = target - 150 * DEG;
  const hi = target - 35 * DEG;
  let rights = 0;
  const turned = (): number => dir * turnOf(pieces);
  for (let k = 0; k < slots; k++) {
    pieces.push(S(rng, sLo, sHi, 1, 10, 170));
    if (k === opts.sigSlot && opts.signature) {
      add(opts.signature);
      continue;
    }
    const room = hi - turned();
    // Behind schedule: must turn now. Out of room: only flicks or dents.
    const need = (lo - turned()) / (slots - k);
    let mod: Module | null = null;
    for (let tries = 0; !mod && tries < 14; tries++) {
      const turns = room > 55 * DEG ? free(TURNS) : [];
      const dents = rights < opts.rights && need < 35 * DEG ? free(DENTS) : [];
      const flicks = need < 60 * DEG ? free(FLICKS) : [];
      const kind = pick<'t' | 'd' | 'f'>(rng, [
        ['t', turns.length ? 3 : 0],
        ['d', dents.length ? 1 : 0],
        ['f', flicks.length ? 1.4 : 0],
      ]);
      const pool = kind === 't' ? turns : kind === 'd' ? dents : flicks;
      if (!pool.length) break;
      const cand = moduleOf(rng, pick(rng, pool), kind === 'd' ? -dir : kind === 'f' ? (rng.chance(0.5) ? 1 : -1) : dir);
      const t = dir * turnOf(cand.pieces);
      if (t > room) continue;
      if (kind === 'd') rights++;
      mod = cand;
    }
    if (!mod) return fail('no-module');
    add(mod);
  }
  // Still short of the budget: one more corner.
  if (turned() < lo) {
    pieces.push(S(rng, sLo, sHi, 1, 10, 170));
    const mod = moduleOf(rng, pick(rng, free(TURNS)), dir);
    const t = turned() + dir * turnOf(mod.pieces);
    if (t < lo || t > hi) return fail('budget');
    add(mod);
  }
  return true;
}

/** How much a module turns the loop's way. Big ones need a lighter first corner and some dents. */
function bigTurn(mod: Module | null, dir: number): number {
  return mod ? dir * turnOf(mod.pieces) : 0;
}

/** A single loop turning once around (+360 degrees), with the day's signature in it. */
function planLoop(rng: Rng, signature: Signature): Plan | null {
  const plan: Plan = { pieces: [], modules: [], parts: [], start: { x: 0, y: 0, h: 0 } };
  const main = S(rng, 145, 190, 2.2, 135, 240);
  main.main = true;
  plan.pieces.push(main);
  plan.parts.push(['main', 0, 0]);
  // Signatures turning against the loop half the time, where that works.
  const sigDir = signature === 'banking' || signature === 'karussell' || signature === 'tripleApex' || signature === 'streets' || signature === 'climb' ? 1 : rng.chance(0.5) ? 1 : -1;
  const sig = moduleOf(rng, signature, sigDir);
  const heavy = bigTurn(sig, 1) > 120 * DEG;
  const t1 = moduleOf(rng, pick(rng, heavy ? HEAVY_BRAKING.filter(([n]) => n === 'ninety' || n === 'tight') : HEAVY_BRAKING), 1);
  plan.pieces.push(...t1.pieces);
  plan.modules.push(t1.name);
  const slots = rng.int(4, 6);
  const sigSlot = rng.int(0, slots - 1);
  if (!planModules(rng, plan, 1, 2 * PI, slots, { signature: sig, sigSlot, rights: heavy ? 2 : rng.int(0, 2), straight: [10, 45] })) return null;
  // The last corner feeds the main straight: whatever turn is left, fast if it can be.
  plan.pieces.push(S(rng, 15, 50, 1, 10, 150));
  plan.pieces.push({ kind: 'a', r: rng.range(40, 85), ang: 1, closing: true });
  plan.pieces.push(S(rng, 12, 30, 0.6, 8, 90));
  plan.modules.push('closing');
  if (!closeChain(rng, plan.pieces, plan.start, { x: 0, y: 0 }, 2 * PI)) return null;
  return plan;
}

/**
 * Figure of eight (the crossover signature): straight X1 heads up-right
 * through the origin, a lobe turns right (clockwise) round to X2, which heads
 * up-left through the origin, and a lobe turning left brings it back to X1.
 * The main straight is in the left lobe.
 */
function planFigure8(rng: Rng): Plan | null {
  const alpha = rng.range(28, 52) * DEG;
  const a1 = rng.range(36, 50);
  const b1 = rng.range(36, 50);
  const a2 = rng.range(36, 50);
  const b2 = rng.range(36, 50);
  const h1 = alpha;
  const h2 = PI - alpha;
  const lobeTurn = PI + 2 * alpha;
  const x1s = { x: -a1 * dcos(h1), y: -a1 * dsin(h1) };
  const x1e = { x: b1 * dcos(h1), y: b1 * dsin(h1) };
  const x2s = { x: -a2 * dcos(h2), y: -a2 * dsin(h2) };
  const x2e = { x: b2 * dcos(h2), y: b2 * dsin(h2) };
  const modules: string[] = [];
  const parts: [string, number, number][] = [];

  const lobe = (dir: number, withMain: boolean): Plan | null => {
    const plan: Plan = { pieces: [S(rng, 16, 34, 0.8, 14, 40)], modules, parts: [], start: { x: 0, y: 0, h: 0 } };
    if (withMain) {
      // The main straight, then the heavy braking at its end.
      plan.pieces.push(S(rng, 20, 55, 1, 14, 140));
      const turn = moduleOf(rng, pick(rng, TURNS.filter(([n]) => n === 'ninety' || n === 'medium' || n === 'sweeper')), dir);
      plan.pieces.push(...turn.pieces);
      modules.push(turn.name);
      // Longer than the X's straights can ever get, so the start line always lands here.
      const main = S(rng, 200, 240, 2.2, 190, 280);
      main.main = true;
      plan.parts.push(['main', plan.pieces.length, plan.pieces.length]);
      plan.pieces.push(main);
      const t1 = moduleOf(rng, pick(rng, HEAVY_BRAKING), dir);
      plan.pieces.push(...t1.pieces);
      modules.push(t1.name);
    }
    if (!planModules(rng, plan, dir, lobeTurn, withMain ? rng.int(0, 1) : rng.int(1, 3), { signature: null, sigSlot: -1, rights: rng.int(0, 1), straight: [14, 50] })) return null;
    plan.pieces.push(S(rng, 16, 45, 1, 12, 130));
    plan.pieces.push({ kind: 'a', r: rng.range(40, 80), ang: dir, closing: true });
    plan.pieces.push(S(rng, 16, 34, 0.8, 14, 40));
    modules.push('closing');
    return plan;
  };
  // Right lobe: from the end of X1 round to the start of X2, turning clockwise.
  const right = lobe(-1, false);
  const left = lobe(1, true);
  if (!right || !left) return null;
  if (!closeChain(rng, right.pieces, { ...x1e, h: h1 }, x2s, -lobeTurn)) return null;
  if (!closeChain(rng, left.pieces, { ...x2e, h: h2 }, x1s, lobeTurn)) return null;
  // Start the chain at the start of X1 so the loop reads X1, right lobe, X2, left lobe.
  const pieces: Piece[] = [S(rng, a1 + b1, a1 + b1, 0), ...right.pieces, S(rng, a2 + b2, a2 + b2, 0), ...left.pieces];
  const leftBase = 2 + right.pieces.length;
  for (const [name, a, b] of left.parts) parts.push([name, leftBase + a, leftBase + b]);
  for (const [name, a, b] of right.parts) parts.push([name, 1 + a, 1 + b]);
  return { pieces, modules, parts, start: { ...x1s, h: h1 } };
}

// ---------------------------------------------------------------------------
// Chain to points
// ---------------------------------------------------------------------------

/**
 * Sample the chain's curvature every metre, ease it (two box passes, so each
 * corner gets a transition), integrate it back into points and spread the
 * small closing gap the easing leaves over the lap. The main straight comes
 * first so later steps find it easily. Banking is sampled the same way and
 * eased over a longer stretch; named parts come back as fractions of the lap.
 */
function chainToPoints(plan: Plan, ease: number): { pts: Vec2[]; bank: number[] | null; sections: Section[] } {
  const { pieces, start } = plan;
  const kappa: number[] = [];
  const bankRaw: number[] = [];
  const pieceStart: number[] = [];
  let mainStart = 0;
  let banked = false;
  for (const p of pieces) {
    pieceStart.push(kappa.length);
    if (p.kind === 's') {
      if (p.main) mainStart = kappa.length;
      const count = Math.max(0, Math.round(p.len));
      for (let k = 0; k < count; k++) {
        kappa.push(0);
        bankRaw.push(0);
      }
    } else {
      const count = Math.max(1, Math.round(p.r * Math.abs(p.ang)));
      // Raised on the outside of the turn: a left turn lifts the right side.
      const b = p.bank ? (p.ang > 0 ? -p.bank : p.bank) : 0;
      if (b !== 0) banked = true;
      // Curvature per metre such that the heading change is exact.
      for (let k = 0; k < count; k++) {
        kappa.push(p.ang / count);
        bankRaw.push(b);
      }
    }
  }
  pieceStart.push(kappa.length);
  const n = kappa.length;
  const smooth = (arr: number[], r: number, passes: number): number[] => {
    let k = arr;
    for (let pass = 0; pass < passes; pass++) {
      const out = new Array<number>(n);
      let sum = 0;
      for (let q = -r; q <= r; q++) sum += k[(q + n) % n];
      for (let i = 0; i < n; i++) {
        out[i] = sum / (2 * r + 1);
        sum += k[(i + r + 1) % n] - k[(i - r + n) % n];
      }
      k = out;
    }
    return k;
  };
  const k = smooth(kappa, ease, 2);
  const bankS = banked ? smooth(bankRaw, 14, 2) : null;
  const xs = new Array<number>(n);
  const ys = new Array<number>(n);
  let x = start.x;
  let y = start.y;
  let h = start.h;
  for (let i = 0; i < n; i++) {
    xs[i] = x;
    ys[i] = y;
    const hm = h + k[i] * 0.5;
    x += dcos(hm);
    y += dsin(hm);
    h += k[i];
  }
  // The easing moves the end by a few metres: spread the gap evenly.
  const gx = x - start.x;
  const gy = y - start.y;
  const pts: Vec2[] = [];
  const bank: number[] | null = bankS ? [] : null;
  for (let q = 0; q < n; q++) {
    const i = (mainStart + q) % n;
    pts.push({ x: xs[i] - (gx * i) / n, y: ys[i] - (gy * i) / n });
    if (bank && bankS) bank.push(bankS[i]);
  }
  const frac = (m: number): number => (((m - mainStart) % n) + n) % n / n;
  const sections = plan.parts.map(([name, a, b]) => ({ name, from: frac(pieceStart[a]), to: frac(pieceStart[b + 1]) }));
  return { pts, bank, sections };
}

export function makeLayoutV3(rng: Rng, scale: number, family: LayoutFamily, signature: Signature): Layout | null {
  // Most random chains can't close without absurd straights; drawing another is cheap.
  let plan: Plan | null = null;
  for (let tries = 0; tries < 60 && !plan; tries++) plan = family === 'figure8' ? planFigure8(rng) : planLoop(rng, signature);
  if (!plan) return null;
  const raw = chainToPoints(plan, 7);
  // Centre, maybe mirror (clockwise), rotate, scale.
  let cx = 0;
  let cy = 0;
  for (const p of raw.pts) {
    cx += p.x / raw.pts.length;
    cy += p.y / raw.pts.length;
  }
  const mirror = rng.chance(0.5) ? -1 : 1;
  const rot = rng.range(0, 2 * PI);
  const cr = dcos(rot);
  const sr = dsin(rot);
  const pts = raw.pts.map((p) => {
    const x = (p.x - cx) * scale;
    const y = (p.y - cy) * mirror * scale;
    return { x: x * cr - y * sr, y: x * sr + y * cr };
  });
  // Mirroring swaps left and right, so the banking's sign flips with it.
  const bank = raw.bank ? raw.bank.map((b) => b * mirror) : null;
  return { family, signature, pts, bank, sections: raw.sections, modules: plan.modules };
}
